/**
 * Searchable index of project settings — powers the search box at the
 * top of Project Settings. Each entry maps the WORDS a first-time user
 * would type (not our internal names) to the tab that holds the
 * setting. Keep labels in plain task-language; pile synonyms into
 * ``keywords`` liberally — matching is substring-based over label +
 * keywords + tab.
 *
 * ``path`` is relative to the project settings base (see
 * Settings/index.jsx menu registration).
 */
import { settingsIndex as extensionSettingsIndex } from "@afriannotate/extensions";

const OWN_INDEX = [
  // ── Quality ──
  {
    label: "Annotators per task (overlap)",
    tab: "Quality",
    path: "/quality",
    keywords:
      "how many annotators needed each task instance overlap consensus multiple annotations maximum annotations agreement votes per item redundancy",
  },
  {
    label: "Agreement metric & thresholds",
    tab: "Quality",
    path: "/quality",
    keywords: "agreement score inter-annotator iaa consensus threshold auto accept quality control ground truth",
  },
  // ── Annotation ──
  {
    label: "Who can annotate",
    tab: "Annotation",
    path: "/annotation",
    keywords: "annotator permission which members label allowed roles annotate access",
  },
  {
    label: "Task ordering & sampling",
    tab: "Annotation",
    path: "/annotation",
    keywords: "task order random sequential sampling distribution queue next task",
  },
  {
    label: "Skip behaviour",
    tab: "Annotation",
    path: "/annotation",
    keywords: "skip tasks requeue ignore leave behaviour",
  },
  {
    label: "Submission requirements (empty annotations)",
    tab: "Annotation",
    path: "/annotation",
    keywords: "empty annotation require at least one region submit disabled mandatory label minimum",
  },
  // ── Labeling ──
  {
    label: "Labeling interface (labels & layout)",
    tab: "Labeling",
    path: "/labeling",
    keywords: "labeling config labels classes choices interface template xml tags editor setup instructions",
  },
  // ── General ──
  {
    label: "Project name & description",
    tab: "General",
    path: "/",
    keywords: "rename title description general info workspace move project appearance logo color",
  },
  // ── Visibility ──
  {
    label: "Who can see this project",
    tab: "Visibility",
    path: "/visibility",
    keywords: "visibility public private discover share link crowdsource open contributions join find",
  },
  // ── Review ──
  {
    label: "Review requirements",
    tab: "Review",
    path: "/review-settings",
    keywords: "review reviewer accept reject verdict approval required queue revisit",
  },
  // ── Governance ──
  {
    label: "Required consent forms",
    tab: "Consent",
    path: "/consent",
    keywords: "consent agreement sign gdpr popia required form annotators privacy",
  },
  {
    label: "Rates & contributor pay",
    tab: "Contributions",
    path: "/contributions",
    keywords: "pay payment rate card money payout earnings ledger compensation price per task",
  },
  {
    label: "Dataset license",
    tab: "Licensing",
    path: "/licensing",
    keywords: "license cc-by terms dataset reuse attribution",
  },
  {
    label: "Security & access controls",
    tab: "Security",
    path: "/security",
    keywords: "security download export permission retention idle logout offline",
  },
  // ── Data & integrations ──
  {
    label: "Cloud storage (import / export)",
    tab: "Cloud Storage",
    path: "/storage",
    keywords: "storage s3 gcs azure bucket sync source target import export files",
  },
  {
    label: "Hugging Face Hub sync",
    tab: "Hugging Face",
    path: "/hugging-face",
    keywords: "hugging face hub dataset push pull hf token",
  },
  {
    label: "Machine learning backend",
    tab: "Model",
    path: "/ml",
    keywords: "ml model backend predictions pre-labeling auto label active learning train",
  },
  {
    label: "Predictions",
    tab: "Predictions",
    path: "/predictions",
    keywords: "predictions pre-annotations model version show annotators",
  },
  {
    label: "Webhooks",
    tab: "Webhooks",
    path: "/webhooks",
    keywords: "webhook callback integration notify external endpoint events",
  },
  // ── Danger ──
  {
    label: "Delete project / reset data",
    tab: "Danger Zone",
    path: "/danger-zone",
    keywords: "delete remove project drop annotations reset danger destroy purge",
  },
];

// Entries for the pages an installed extension adds.
export const SETTINGS_INDEX = [...OWN_INDEX, ...(extensionSettingsIndex ?? [])];

/** Substring match over label + keywords + tab; returns ranked entries. */
export const searchSettings = (query, limit = 6) => {
  const q = (query || "").trim().toLowerCase();
  if (q.length < 2) return [];
  const terms = q.split(/\s+/).filter(Boolean);
  const scored = [];
  for (const entry of SETTINGS_INDEX) {
    const hay = `${entry.label} ${entry.keywords} ${entry.tab}`.toLowerCase();
    let score = 0;
    for (const t of terms) {
      if (!hay.includes(t)) {
        score = 0;
        break;
      }
      // label hits outrank keyword hits
      score += entry.label.toLowerCase().includes(t) ? 3 : 1;
    }
    if (score > 0) scored.push({ entry, score });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((s) => s.entry);
};
